import api from '../../../api';

// Create class with lab batch configuration
export const createClass = async (payload) => {
  try {
    console.log('🚀 Creating class with payload:', payload);
    
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await api.post('/api/classManager/classes', payload, { headers });
    
    console.log('✅ Class created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating class:', error);
    throw error;
  }
};

// Create lab batches for existing class
export const createLabBatches = async (labBatchData) => {
  try {
    console.log('🚀 Creating lab batches with data:', labBatchData);
    
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await api.post('/api/classManager/lab-batches', labBatchData, { headers });
    
    console.log('✅ Lab batches created successfully:', response.data);
    
    return {
      success: true,
      ...response.data,
      classUpdated: response.data.classUpdated || null,
      labBatchIds: response.data.classUpdated?.labBatchIds || []
    };
  } catch (error) {
    console.error('❌ Error creating lab batches:', error);
    
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    };
    
    console.error('❌ Error details:', errorDetails);
    
    return {
      success: false,
      error: error,
      message: error.response?.data?.message || error.message || 'Failed to create lab batches',
      details: errorDetails
    };
  }
};

// Delete class if lab batch creation fails
export const deleteClass = async (classId) => {
  try {
    console.log('🗑️ Deleting class:', classId);
    
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await api.delete(`/api/classManager/class/${classId}`, { headers });
    
    console.log('✅ Class deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting class:', error);
    throw error;
  }
};

// Fixed function to fetch department subjects
export const fetchDepartmentSubjects = async ({ 
  tenantId, 
  departmentId, 
  academicYearId, 
  semesterId = null,
  termId = null,
  token = null 
}) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    const params = {
      tenantId,
      academicYearId,
      departmentId,
      includeCrossSemester: 'true'
    };

    if (semesterId) {
      params.semesterId = semesterId;
    }
    if (termId) {
      params.termId = termId;
    }

    const response = await api.get('/api/subjects/class-manager/subjects/department/context', {
      params,
      headers
    });

    // Handle different response formats
    const data = response.data;
    const subjects = data?.data?.subjects?.all || data?.subjects || data || [];
    
    return {
      success: true,
      subjects: Array.isArray(subjects) ? subjects : []
    };
  } catch (error) {
    console.error('Error fetching department subjects:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      subjects: []
    };
  }
};

// Fetch program mapping with term support
export async function fetchProgramMapping(params) {
  // params: { tenantId, programId, programTypeId, academicYearId, semesterId?, termId? }
  try {
    console.log('🔍 Fetching program mapping with params:', params);
    const res = await api.get('/api/program-subject-mappings/grouped', { params });
    return res.data;
  } catch (error) {
    console.error('❌ Error fetching program mapping:', error);
    throw error;
  }
}

// Fetch classes for a tenant
export const fetchClasses = async (tenantId) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/api/classManager/classes/${tenantId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Fetch classes for academic year with term support
export const fetchClassesForAcademicYear = async (academicYearId, params = {}) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/api/classManager/classes-for-academic-year/${academicYearId}`, {
    params,
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Fetch classes for semester
export const fetchClassesForSemester = async (semesterId, params = {}) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/api/classManager/classes-for-semester/${semesterId}`, {
    params,
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// NEW: Fetch classes for term
export const fetchClassesForTerm = async (termId, params = {}) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/api/classManager/classes-for-term/${termId}`, {
    params,
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Update class
export const updateClass = async (classId, payload) => {
  const token = localStorage.getItem('token');
  const response = await api.put(`/api/classManager/class/${classId}`, payload, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Assign faculty
export const assignFaculty = async (classId, facultyAssignments) => {
  const token = localStorage.getItem('token');
  const response = await api.put(`/api/classManager/assign-faculty/${classId}`, {
    facultyAssignments
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Unassign faculty
export const unassignFaculty = async (classId, facultyId) => {
  const token = localStorage.getItem('token');
  const response = await api.delete(`/api/classManager/unassign-faculty/${classId}/${facultyId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Fetch all classes for context with term support
export const fetchAllClassesForContext = async ({
  tenantId,
  academicYearId,
  programId,
  programTypeId,
  semesterId,
  termId,
  isSemesterBased
}) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  let allClasses = [];

  // Fetch semester-based classes if semesterId is provided
  if (isSemesterBased && semesterId) {
    const semRes = await api.get(`/api/classManager/classes-for-semester/${semesterId}`, {
      params: { programTypeId, programId },
      headers
    });
    const semClasses = semRes.data?.classes || semRes.data || [];
    allClasses = allClasses.concat(Array.isArray(semClasses) ? semClasses : []);
  }

  // Fetch term-based classes if termId is provided
  if (!isSemesterBased && termId) {
    const termRes = await api.get(`/api/classManager/classes-for-term/${termId}`, {
      params: { programTypeId, programId },
      headers
    });
    const termClasses = termRes.data?.classes || termRes.data || [];
    allClasses = allClasses.concat(Array.isArray(termClasses) ? termClasses : []);
  }

  // Always fetch annual classes for the academic year
  const annualRes = await api.get(`/api/classManager/classes-for-academic-year/${academicYearId}`, {
    params: { programTypeId, programId, isSemesterBased: 'false' },
    headers
  });
  const annualClasses = annualRes.data?.classes || annualRes.data || [];
  allClasses = allClasses.concat(
    (Array.isArray(annualClasses) ? annualClasses : []).filter(cls => !cls.isSemesterBased && !cls.termId)
  );

  return allClasses;
};

// Get class details
export const getClassDetails = async (classId) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/api/classManager/class/${classId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};